import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  Autocomplete,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import {
  Business,
  Build,
  Groups,
  Science,
  LocalShipping,
} from "@mui/icons-material";
import TimeInput24H from "../../../components/TimeInput24H";
import { CALENDAR_EVENT_TYPES, STATUS_CONFIG } from "../constants";

const EVENT_TYPE_ICONS = {
  OFFICE_VISIT: Business,
  MECHANIC_WORKSHOP: Build,
  MEETING: Groups,
  LAB_STUDY: Science,
  UNIT_DELIVERY: LocalShipping,
  MAINTENANCE: Build,
};

export default function EventForm({
  formData,
  onChange,
  technicians = [],
}) {
  const selectedTechs = Array.isArray(formData.technicians)
    ? formData.technicians
    : [];
  const allSelected =
    technicians.length > 0 && selectedTechs.length === technicians.length;

  const handleAllTechniciansChange = (e) => {
    const checked = e.target.checked;
    onChange("technicians", checked ? technicians : []);
  };

  const handleTypeChange = (eventType) => {
    const prevType = CALENDAR_EVENT_TYPES.find(
      (t) => t.value === formData.eventType
    );
    const nextType = CALENDAR_EVENT_TYPES.find((t) => t.value === eventType);
    const titleIsDefault =
      !formData.title || formData.title === prevType?.label;
    onChange("eventType", eventType);
    if (titleIsDefault && nextType) {
      onChange("title", nextType.label);
    }
  };

  return (
    <Stack sx={{ mt: 1, gap: 2 }}>
      <TextField
        select
        label="Tipo de evento"
        value={formData.eventType}
        onChange={(e) => handleTypeChange(e.target.value)}
        fullWidth
        size="small"
      >
        {CALENDAR_EVENT_TYPES.map((eventType) => {
          const Icon = EVENT_TYPE_ICONS[eventType.value] || Build;
          return (
            <MenuItem key={eventType.value} value={eventType.value}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Icon sx={{ fontSize: 20, color: eventType.color }} />
                {eventType.label}
              </Box>
            </MenuItem>
          );
        })}
      </TextField>

      <TextField
        label="Título"
        value={formData.title || ""}
        onChange={(e) => onChange("title", e.target.value)}
        fullWidth
        size="small"
        required
        placeholder="Ej: Visita a oficina, visita a taller..."
      />

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              onChange={handleAllTechniciansChange}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Asignar a todos los técnicos
            </Typography>
          }
        />
        <Autocomplete
          multiple
          options={technicians}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          value={selectedTechs}
          onChange={(_, newValue) => onChange("technicians", newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option._id}
                label={option.name}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Técnicos"
              size="small"
              placeholder={selectedTechs.length ? "" : "Selecciona uno o varios"}
              required={selectedTechs.length === 0}
            />
          )}
          fullWidth
        />
      </Box>

      <TextField
        label="Ubicación (opcional)"
        value={formData.location || ""}
        onChange={(e) => onChange("location", e.target.value)}
        fullWidth
        size="small"
        placeholder="Oficina, taller, sucursal..."
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          type="date"
          label="Fecha"
          value={formData.date}
          onChange={(e) => onChange("date", e.target.value)}
          fullWidth
          size="small"
          required
          InputLabelProps={{ shrink: true }}
        />
        <TimeInput24H
          label="Hora (24H)"
          value={formData.hour}
          onChange={(e) => onChange("hour", e.target.value)}
          fullWidth
          size="small"
          required
        />
      </Box>

      <TextField
        select
        label="Estado"
        value={formData.status}
        onChange={(e) => onChange("status", e.target.value)}
        fullWidth
        size="small"
      >
        {Object.entries(STATUS_CONFIG).map(([value, status]) => (
          <MenuItem key={value} value={value}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: status.color,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {status.label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Notas"
        value={formData.notes || ""}
        onChange={(e) => onChange("notes", e.target.value)}
        fullWidth
        multiline
        rows={3}
        size="small"
        placeholder="Observaciones para la procedia..."
      />
    </Stack>
  );
}
